import type {Cell, Graph} from '@antv/x6';
import type ModelerX6 from '@core_x6/lib/ModelerX6';

export type CanvasFindResult = {
	id: string;
	cell: Cell | null;
	viewElement: unknown;
	x6Json: ReturnType<Cell['toJSON']> | null;
	isNode: boolean;
	isEdge: boolean;
};

export type CanvasContextGlobal = {
	/** Ячейка X6 и viewElement модели по id (нода, связь и т.д.). */
	find: (id: string | number) => CanvasFindResult | null;
	getModeler: () => ModelerX6;
	getGraph: () => Graph | null | undefined;
};

declare global {
	interface Window {
		CanvasContext?: CanvasContextGlobal;
	}
}

function getCellById(graph: Graph | null | undefined, id: string): Cell | null {
	if (!graph) return null;
	const fromGraph = graph.getCellById?.(id);
	if (fromGraph) return fromGraph;
	const model = graph.model as {getCellById?: (cellId: string) => Cell | undefined} | undefined;
	const fromModel = model?.getCellById?.(id);
	return fromModel ?? null;
}

/**
 * Вешает на `window.CanvasContext` API отладки (консоль браузера).
 * Снимается возвращённой функцией при размонтировании редактора.
 */
export function installCanvasContext(modeler: ModelerX6): () => void {
	if (typeof window === 'undefined') {
		return () => {};
	}

	const api: CanvasContextGlobal = {
		find(id) {
			if (id === '' || id === null || id === undefined) {
				return null;
			}
			const sid = String(id);
			const graph = modeler.getGraph?.();
			const cell = getCellById(graph, sid);
			const viewElement =
				typeof modeler._findViewElementById === 'function'
					? modeler._findViewElementById(sid)
					: null;
			if (!cell && viewElement == null) {
				return null;
			}
			const isNode = Boolean(cell && typeof cell.isNode === 'function' && cell.isNode());
			const isEdge = Boolean(cell && typeof cell.isEdge === 'function' && cell.isEdge());
			return {
				id: sid,
				cell,
				viewElement,
				x6Json: cell && typeof cell.toJSON === 'function' ? cell.toJSON() : null,
				isNode,
				isEdge,
			};
		},
		getModeler: () => modeler,
		getGraph: () => modeler.getGraph?.(),
	};

	window.CanvasContext = api;

	return () => {
		if (window.CanvasContext === api) {
			delete window.CanvasContext;
		}
	};
}
